import React, { useState, useEffect, useRef, createContext } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSidebar } from "../context/SidebarContext";
import { useNotification } from "../context/NotificationContext";
import {
  FaHome,
  FaTachometerAlt,
  FaUserMd,
  FaUserNurse,
  FaUser,
  FaHospital,
  FaClock,
  FaFlask,
  FaFileAlt,
  FaFolder,
  FaClipboardList,
  FaBars,
  FaTimes,
  FaEnvelopeOpenText,
  FaFilePdf 
} from "react-icons/fa";
import "./Sidebar.scss";

const SIDEBAR_COLLAPSED = 1;
const SIDEBAR_EXPANDED = 1;

export const SidebarWidthContext = createContext(SIDEBAR_COLLAPSED);

const Sidebar = ({ onWidthChange }) => {
  const { user } = useAuth();
  const { unreadCount } = useNotification();
  const location = useLocation();
  

  const [collapsed, setCollapsed] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const { mobileOpen, setMobileOpen } = useSidebar();

  //Adjust sidebar width
  useEffect(() => {
    if (onWidthChange) {
      if (isMobile) {
        onWidthChange(mobileOpen ? SIDEBAR_EXPANDED : 0);
      } else {
        onWidthChange(collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_EXPANDED);
      }
    }
  }, [collapsed, onWidthChange, isMobile, mobileOpen]);

  const sidebarRef = useRef(null);

  // Auto close on route change (mobile)
  useEffect(() => {
    if (isMobile) {
      setMobileOpen(false);
      setCollapsed(true);
    }
  }, [location.pathname, isMobile]);
  
  // Menu items by roles
  const menus = {
    1: [
      { icon: <FaTachometerAlt />, label: "Dashboard", to: "/admin" },
      { icon: <FaHospital />, label: "Create Sub Hospital", to: "/admin-subhospital" },
      { icon: <FaUser />, label: "Create Users", to: "/manage-users" },
      { icon: <FaUserMd />, label: "Doctor Availability", to: "/admin_doctor" },
      { icon: <FaUserNurse />, label: "Receptionist Manage", to: "/receptionist_management" },
      { icon: <FaUser />, label: "Patient", to: "/admin_patient_list" },
      { icon: <FaClock />, label: "Watch Time", to: "/watch-time" },
      { icon: <FaFlask />, label: "Lab Test", to: "/addlabTest" },

    ],
    2: [
      { icon: <FaHome />, label: "Doctor Dashboard", to: "/doctor" },
      { icon: <FaFileAlt />, label: "Patient Report", to: "/patient_report" },
      { icon: <FaClock />, label: "Watch Time", to: "/watch-time" },
    ],
    3: [
      { icon: <FaHome />, label: "Dashboard", to: "/receptionist" },
      { icon: <FaClipboardList />, label: "Patient Appointment", to: "/receptionist_patient_appointment" },
      { icon: <FaUser />, label: "Book Appointment", to: "/receptionist/book-appointment" },
      { icon: <FaClock />, label: "Watch Time", to: "/watch-time" },
    ],
    4: [
      { icon: <FaHome />, label: "Patient Dashboard", to: "/patient" },
      { icon: <FaFileAlt />, label: "Prescriptions", to: "/prescription" },
      { icon: <FaFolder />, label: "Patient Appointment", to: "/patient_appointment" },
      { icon: <FaClipboardList />, label: "Patient Report", to: "/patient_report" },
    ],
    5: [
      { icon: <FaHome />, label: "Lab Dashboard", to: "/lab" },
      { icon: <FaFileAlt />, label: "Reports", to: "/report" },
    ],
    6:[
      {icon: <FaHome />, label: "Home", to: "/super_admin"},
      { icon: <FaEnvelopeOpenText  />, label: "Enquire-Page", to: "/enquire-page" },
      { icon: <FaFilePdf   />, label: "Brochure-Page", to: "/brochure-page" },
      { icon: <FaEnvelopeOpenText />, label: "Blogs", to: "/blogs" },


    ]
  };

  const currentMenu = menus[user?.role_id] || [];
  const expanded = !collapsed || (isMobile && mobileOpen);

  // Close sidebar when clicking outside (desktop & mobile)
  useEffect(() => {
    if (!expanded) return undefined;

    const handleOutsideClick = (e) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target)) {
        if (isMobile) {
          setMobileOpen(false);
        } else {
          setCollapsed(true);
        }
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [expanded, isMobile, setMobileOpen]);

  

  
  // If not logged in or on login page, don't render sidebar (after hooks)
  if (location.pathname === "/login" || !user) return null;

  return (
    <SidebarWidthContext.Provider
       value={collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_EXPANDED}
    >
      {/* Mobile overlay */}
      {isMobile && mobileOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <nav
        ref={sidebarRef}
        className={`custom-sidebar ${expanded ? "expanded" : "collapsed"} ${
          isMobile && mobileOpen ? "mobile-open" : ""
        }`}
        style={{
          width: expanded ? SIDEBAR_EXPANDED : SIDEBAR_COLLAPSED,
        }}
      >
        {/* Toggle only for desktop */}
        <button
        className={`sidebar-toggle-btn ${isMobile ? "hide-toggle" : ""}`}
        onClick={() => setCollapsed((prev) => !prev)}
      >
        {collapsed ? <FaBars /> : <FaTimes />}
      </button>

        <ul className="sidebar-menu">
          {currentMenu.map((item, idx) => (
            <li
              key={idx}
              className={`sidebar-menu-item ${
                location.pathname === item.to ? "active" : ""
              }`}
            >
              <Link
                to={item.to}
                className="sidebar-link"
                onClick={() => {
                  
                    setCollapsed(true);
                    setMobileOpen(false);
                    
                  
                }}
              >
                <span className="sidebar-icon">{item.icon}</span>
                {expanded && <span className="sidebar-label">{item.label}</span>}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    </SidebarWidthContext.Provider>
  );
};

export default Sidebar;
